import { useState } from 'react';
import { Body1, Button, Caption1, makeStyles, tokens } from '@fluentui/react-components';
import { DismissRegular, DocumentRegular, FolderRegular } from '@fluentui/react-icons';

import { SelectItemDialog, TItem, TSelectItemDialogState } from '@modules/items';

const useStyles = makeStyles({
  list: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXXS,
  },
  row: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: tokens.spacingHorizontalM,
    padding: `${tokens.spacingVerticalXS} ${tokens.spacingHorizontalM}`,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: tokens.colorNeutralBackground2,
  },
  muted: {
    color: tokens.colorNeutralForeground3,
  },
});

type TProps = {
  folderId?: string;
  items: TItem[];
  onChange: (items: TItem[]) => void;
};

export const ScopeField = ({ folderId, items, onChange }: TProps) => {
  const classes = useStyles();
  const [dialogState, setDialogState] = useState<TSelectItemDialogState | undefined>(undefined);

  const openDialog = () =>
    setDialogState({
      isOpen: true,
      onSelect: async (selectedItems) => {
        onChange(selectedItems);
        setDialogState((previous) => previous && { ...previous, isOpen: false });
      },
    });

  return (
    <div className="flex flex-col gap-3">
      <SelectItemDialog
        initialFolderId={folderId}
        initialSelectedItems={items}
        state={dialogState}
        setState={setDialogState}
        multiSelect
        enableSearch
        title="Select scope"
        actionText="Select"
        navigatableFolderTypes={['NORMAL', 'COUNTRY']}
        selectableTypes={['FOLDER', 'DOCUMENT', 'FILE']}
      />

      {items.length > 0 ? (
        <div className={classes.list}>
          {items.map((item) => (
            <div key={item._id} className={classes.row}>
              <div className="flex items-center gap-2 min-w-0">
                {item.type === 'FOLDER' ? <FolderRegular /> : <DocumentRegular />}
                <Body1 className="truncate">{item.name}</Body1>
              </div>
              <Button
                appearance="subtle"
                icon={<DismissRegular />}
                aria-label={`Remove ${item.name}`}
                onClick={() => onChange(items.filter((selected) => selected._id !== item._id))}
              />
            </div>
          ))}
        </div>
      ) : (
        <Caption1 className={classes.muted}>Nothing selected</Caption1>
      )}

      <Button appearance="secondary" className="w-fit" onClick={openDialog}>
        {items.length > 0 ? 'Change selection' : 'Select items'}
      </Button>
    </div>
  );
};
